/**
 * The Day timeline — a DRY RUN of what the planner would lay out today:
 * the warnings folded into one line, the day in numbers and lanes, the
 * lists in use, then every drop with its list (19/10/2026).
 * Degrades honestly: skeleton while loading, "preview unavailable" + Retry
 * on failure, cause-specific empty states; never a toast for a read.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Button, Card } from '@/components/ui';

import type {
  DayPlanPreview,
  DropGenPlanDetail,
} from '../../drop-generator.types';
import { describeContainerDrops } from '../../utils/drops/describe-drops';
import { TimelineHeader } from './header/TimelineHeader';
import { TimelineBand } from './TimelineBand';
import { DayOverview } from './summary/DayOverview';
import { TimelineWarnings } from './warnings/TimelineWarnings';
import { TimelineRow } from './TimelineRow';
import { TIMELINE_GRID } from './timeline-grid';

interface Props {
  plan: DropGenPlanDetail;
  preview: DayPlanPreview | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  onResetSlot?: (slotId: string) => void;
  onSkipSlot?: (slotId: string) => void;
  onDisableSlot?: (containerId: string, slotId: string) => void;
  /** The open campaign's name; the preview is already narrowed to it. */
  scopeLabel?: string | null;
}

export const TimelineCard: FC<Props> = ({
  plan,
  preview,
  loading,
  failed,
  onRetry,
  onResetSlot,
  onSkipSlot,
  onDisableSlot,
  scopeLabel = null,
}) => (
  <Card className="overflow-hidden">
    <TimelineHeader plan={plan} preview={preview} loading={loading} onRetry={onRetry} />

    {preview ? <TimelineWarnings preview={preview} /> : null}
    {preview && preview.containers.some((c) => c.drops.length > 0) ? (
      <DayOverview preview={preview} />
    ) : null}

    {loading && !preview ? (
      <div className="flex flex-col gap-1.5 p-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-9 animate-pulse rounded-md bg-muted/60" />
        ))}
      </div>
    ) : failed && !preview ? (
      <div className="flex flex-col items-start gap-2 p-6">
        <p className="text-sm text-muted-foreground">preview unavailable</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    ) : preview && preview.containers.every((c) => c.drops.length === 0) ? (
      <div className="flex flex-col items-start gap-2 p-6">
        <p className="text-sm font-medium">
          {scopeLabel ? `Nothing of ${scopeLabel} would be laid out today.` : 'Nothing would be laid out today.'}
        </p>
        <p className="max-w-xl text-sm text-muted-foreground">
          {plan.containers.length === 0
            ? 'The plan has no containers yet — add one on the Board tab.'
            : 'Every enabled container planned zero drops — check the warnings above (no linked groups, no matching lists, or no capacity).'}
        </p>
      </div>
    ) : preview ? (
      <div className="overflow-x-auto">
        <div className="min-w-[1200px]">
          <div
            className={`${TIMELINE_GRID} border-b border-border/60 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}
          >
            <span />
            <span>Time</span>
            <span>#</span>
            <span>List</span>
            <span>Notes</span>
            <span>Gateway</span>
            <span>Senders</span>
            <span>Texts</span>
            <span className="text-right">Amount</span>
            <span className="text-right">$ / SMS</span>
            <span className="text-right">Est. $</span>
          </div>
          {preview.containers
            .filter((c) => c.drops.length > 0 || c.groups.length > 0)
            .map((container) => {
              const config = plan.containers.find(
                (c) => c.id === container.containerId,
              );
              return (
                <div key={container.containerId}>
                  <TimelineBand
                    container={container}
                    fact={config ? describeContainerDrops(config) : null}
                  />
                  {container.drops.map((drop, index) => (
                    <TimelineRow
                      onResetSlot={onResetSlot}
                      onSkipSlot={onSkipSlot}
                      onDisableSlot={onDisableSlot}
                      key={`${drop.slotId}-${index}`}
                      container={container}
                      drop={drop}
                      index={index}
                      runDate={preview.runDate}
                      recipientTimezone={plan.recipientTimezone}
                    />
                  ))}
                </div>
              );
            })}
        </div>
      </div>
    ) : null}
  </Card>
);
